import React, { useEffect, useRef } from "react";
import { X } from "lucide-react";

/**
 * Simple video trailer modal. Renders a <video> if `src` is provided;
 * otherwise shows a friendly "coming soon" placeholder. Locks body scroll
 * while open and closes on Escape.
 */
export default function TrailerModal({ open, onClose, title = "Trailer", src, poster }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
      if (videoRef.current) {
        try { videoRef.current.pause(); } catch (_) {}
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid="trailer-modal"
      className="fixed inset-0 z-[110] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="absolute inset-0 bg-black/85 backdrop-blur-md"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-5xl">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close trailer"
          data-testid="trailer-modal-close"
          className="absolute -top-3 -right-3 md:-top-4 md:-right-4 z-10 h-10 w-10 rounded-full bg-white text-black hover:bg-white/90 flex items-center justify-center shadow-2xl"
        >
          <X size={18} />
        </button>
        <div className="ycn-glass rounded-2xl overflow-hidden border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)]">
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <div className="font-semibold text-sm text-white/90 truncate">{title}</div>
          </div>
          <div className="aspect-video bg-black relative">
            {src ? (
              <video
                ref={videoRef}
                data-testid="trailer-modal-video"
                src={src}
                poster={poster}
                controls
                autoPlay
                playsInline
                className="w-full h-full object-contain bg-black"
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" data-testid="trailer-modal-empty">
                <div className="font-display text-2xl md:text-3xl font-black ycn-gradient-text mb-2">Trailer Coming Soon</div>
                <p className="text-white/60 text-sm max-w-md">
                  The official trailer will be available here shortly. Check back in a moment.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
